import { useCallback, useState } from "react";

import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";

// custom
import { ValidationErr } from "graphql/generated";
import { uniqueErrorMessages } from "lib/uniqueErrorMessages";

type ErrMsgsProps = {
  errs: (string | undefined)[] | undefined;
  ng: boolean;
  setNg: (value: boolean) => void;
  marginTop?: number;
  sx?: SxProps<Theme>;
};

const ErrMsgs: React.FC<ErrMsgsProps> = (props) => {
  let { errs } = props;
  const { ng, setNg, marginTop, sx } = props;

  if (!errs) return <></>;

  errs = uniqueErrorMessages(errs);

  // https://mui.com/material-ui/react-snackbar/ 公式だとactionをpropsで渡しているが、childrenがいる場合使えないようなのでchildrenにCloseIconを渡す。
  // https://github.com/mui/material-ui/issues/28918 SnackBarのエラーでReact.Fragmentでchildrenを囲むとエラーになるので注意。
  // pre-wrapでmsgの改行を反映させています
  return errs.length > 0 ? (
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      open={ng}
      sx={[marginTop ? { marginTop } : {}, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Alert severity="error" sx={{ my: 1 }}>
        <Stack direction="row" alignItems="center">
          <Stack>
            {errs.map((msg, idx) => (
              <Typography key={idx} sx={{ whiteSpace: "pre-wrap" }}>
                {msg}
              </Typography>
            ))}
          </Stack>
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setNg(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Alert>
    </Snackbar>
  ) : (
    <></>
  );
};

// 上のSnackbarとセットで使うもの。
// reactHookFormを使ってる場合はng, segNgだけで良いのでこちらの利用は不要
//   - reactHookFormの例 /components/supplier/SupplierNew.tsx
//   - そうでない例 /pages/profile/index.tsx
export const useErrMsgs = <T extends string | ValidationErr>() => {
  const [errs, setErrs] = useState<T[]>([]);
  const [ng, setNg] = useState(false);

  const clearAndCloseErrors = useCallback(() => {
    setNg(false);
    setErrs([]);
  }, []);
  const setAndOpenErrors = useCallback((errors: T[]) => {
    setNg(true);
    setErrs(uniqueErrorMessages(errors));
  }, []);
  // ValidationErrを取り扱うhookにstringだけのerrを混ぜたい時に使う。
  const toValidationErrs = useCallback((errors: string[]): ValidationErr[] => {
    return errors.map((err) => {
      return {
        path: "",
        message: err,
        shortMessage: err,
      };
    });
  }, []);

  return { errs, ng, setNg, clearAndCloseErrors, setAndOpenErrors, toValidationErrs };
};

export default ErrMsgs;
